import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { GlobalToastTypeNames, ToastTypes } from 'types';
import {
  useDebtorCandidatesListQuery,
  useIssueLmsBlockMutation,
  useLiftLmsBlockMutation,
  DebtorCandidatesListDocument,
} from 'core/graphql';

import { buildLmsBlockVariables } from 'core/utils/lmsSuspension';

import { useToasts } from '../Toast/useToasts';
import { lmsBlockErrorText } from './lmsBlockErrors';
import type { ILmsBlockActionOptions } from './useLmsAccessBlock';

export interface IDebtorCandidate {
  studentId: string;
  fullName: string;
  email: string;
  overdueAmount: number;
  overdueInvoiceCount: number;
  maxOverdueDays: number;
  currentlyBlocked: boolean;
}

interface IUseGroupDebtorCandidates {
  candidates: IDebtorCandidate[];
  loading: boolean;
  mutating: boolean;
  block: (
    studentId: string,
    options?: ILmsBlockActionOptions,
  ) => Promise<boolean>;
  unblock: (
    studentId: string,
    options?: ILmsBlockActionOptions,
  ) => Promise<boolean>;
}

const fullNameOf = (
  user?: {
    firstName?: string | null;
    lastName?: string | null;
    middleName?: string | null;
  } | null,
): string =>
  [user?.lastName, user?.firstName, user?.middleName]
    .filter(Boolean)
    .join(' ') || '—';

/**
 * Debtor candidates for a group (spec 0003) + per-row block/unblock. Candidates
 * already carry `currentlyBlocked`, so the row needs no second query; the
 * mutations refetch this query so the toggle repaints.
 */
export const useGroupDebtorCandidates = (
  groupId?: string,
): IUseGroupDebtorCandidates => {
  const { t } = useTranslation();
  const { addToast } = useToasts();

  const { data, loading } = useDebtorCandidatesListQuery({
    variables: { groupId },
    skip: !groupId,
    fetchPolicy: 'cache-and-network',
  });

  const opts = useMemo(
    () => ({
      refetchQueries: groupId
        ? [{ query: DebtorCandidatesListDocument, variables: { groupId } }]
        : [],
      awaitRefetchQueries: true,
    }),
    [groupId],
  );

  const [blockMut, blockState] = useIssueLmsBlockMutation(opts);
  const [unblockMut, unblockState] = useLiftLmsBlockMutation(opts);

  const notifyError = useCallback(
    (text?: string) => {
      addToast({
        data: {
          type: ToastTypes.error,
          title: t('pages.studentDetail.lmsBlock.failed'),
          text,
        },
        type: GlobalToastTypeNames.LmsAccessBlock,
      });
    },
    [addToast, t],
  );

  const notifySuccess = useCallback(
    (title: string) => {
      addToast({
        data: {
          type: ToastTypes.success,
          title,
        },
        type: GlobalToastTypeNames.LmsAccessBlock,
      });
    },
    [addToast],
  );

  const block = useCallback(
    async (studentId: string, options?: ILmsBlockActionOptions) => {
      try {
        const res = await blockMut({
          variables: buildLmsBlockVariables(studentId, options),
        });
        const payload = res.data?.blockStudentLmsAccess;
        if (payload?.success) {
          notifySuccess(t('pages.studentDetail.lmsBlock.blocked'));
          return true;
        }
        notifyError(lmsBlockErrorText(payload?.customErrors, t));
        return false;
      } catch (e) {
        notifyError((e as Error).message);
        return false;
      }
    },
    [blockMut, notifyError, notifySuccess, t],
  );

  const unblock = useCallback(
    async (studentId: string, options?: ILmsBlockActionOptions) => {
      try {
        const res = await unblockMut({
          variables: buildLmsBlockVariables(studentId, options),
        });
        const payload = res.data?.unblockStudentLmsAccess;
        if (payload?.success) {
          notifySuccess(t('pages.studentDetail.lmsBlock.unblocked'));
          return true;
        }
        notifyError(lmsBlockErrorText(payload?.customErrors, t));
        return false;
      } catch (e) {
        notifyError((e as Error).message);
        return false;
      }
    },
    [unblockMut, notifyError, notifySuccess, t],
  );

  const candidates = useMemo<IDebtorCandidate[]>(
    () =>
      (data?.debtorCandidates || []).map((row) => ({
        studentId: row.student.id,
        fullName: fullNameOf(row.student.user),
        email: row.student.user?.email || '',
        overdueAmount: row.overdueAmount,
        overdueInvoiceCount: row.overdueInvoiceCount,
        maxOverdueDays: row.maxOverdueDays,
        currentlyBlocked: row.currentlyBlocked,
      })),
    [data?.debtorCandidates],
  );

  return {
    candidates,
    loading,
    mutating: blockState.loading || unblockState.loading,
    block,
    unblock,
  };
};
